/**
 * Route preservation utilities.
 * Handles capturing and restoring routes during auth rehydration.
 */

import { getBrowserAdapter } from '@/lib/browserAdapter'

const STORAGE_KEY = 'ungloss-io:intendedRoute'

/**
 * Public routes that should not be preserved.
 * These routes handle their own redirects or are entry points.
 */
const PUBLIC_ROUTES = ['/', '/login', '/register']

/**
 * Captures the current route for preservation during auth rehydration.
 * Only captures protected routes (non-public routes).
 *
 * @param location - The current location object from react-router
 * @returns The route path to preserve, or null if route should not be preserved
 */
export function captureIntendedRoute(location: {
    pathname: string
    search?: string
    hash?: string
}): string | null {
    const { pathname, search = '', hash = '' } = location

    // Don't preserve public routes
    if (PUBLIC_ROUTES.includes(pathname)) {
        return null
    }

    // Build full path with query string and hash
    const fullPath = `${pathname}${search}${hash}`

    // Store in sessionStorage for persistence across page reloads
    try {
        const browserAdapter = getBrowserAdapter()
        browserAdapter.sessionStorage.setItem(STORAGE_KEY, fullPath)
        return fullPath
    } catch (error) {
        // sessionStorage unavailable (e.g., private browsing mode)
        console.warn('Failed to store intended route:', error)
        return null
    }
}

/**
 * Retrieves the preserved route, if any.
 *
 * @returns The preserved route path, or null if none exists
 */
export function getIntendedRoute(): string | null {
    try {
        const browserAdapter = getBrowserAdapter()
        const route = browserAdapter.sessionStorage.getItem(STORAGE_KEY)
        return route || null
    } catch (error) {
        // sessionStorage unavailable
        console.warn('Failed to retrieve intended route:', error)
        return null
    }
}

/**
 * Clears the preserved route.
 * Should be called after the route has been navigated to or when auth fails.
 */
export function clearIntendedRoute(): void {
    try {
        const browserAdapter = getBrowserAdapter()
        browserAdapter.sessionStorage.removeItem(STORAGE_KEY)
    } catch (error) {
        // sessionStorage unavailable
        console.warn('Failed to clear intended route:', error)
    }
}
